import { Checkbox, Form, Input, Select, Switch, TimePicker } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useOrganizationTypes } from "@/modules/organization-types/hooks/useOrganizationTypes";
import { useSoatos } from "@/modules/soato/hooks/useSoatos";
import { PhoneInput } from "@/shared/components/PhoneInput";
import { useOrganizationsStore } from "../store/organizations.store";
import type { CreateOrganizationRequest } from "../types";

// Clearing an optional text input must send null (not "") so the backend resets the column
const emptyToNull = (value: unknown) => (value === "" ? null : value);

const WORK_DAYS = [
	{ value: 1, label: "Dushanba" },
	{ value: 2, label: "Seshanba" },
	{ value: 3, label: "Chorshanba" },
	{ value: 4, label: "Payshanba" },
	{ value: 5, label: "Juma" },
	{ value: 6, label: "Shanba" },
	{ value: 7, label: "Yakshanba" },
];

export function OrganizationForm({
	form,
}: {
	form: ReturnType<typeof Form.useForm<CreateOrganizationRequest>>[0];
}) {
	const editingItem = useOrganizationsStore((s) => s.editingItem);
	const createDefaults = useOrganizationsStore((s) => s.createDefaults);
	const regionId = Form.useWatch("regionId", form);

	const { data: orgTypesData, isLoading: orgTypesLoading } = useOrganizationTypes({
		page: 1,
		limit: 100,
	});

	// Viloyatlar (parentId yo'qlar)
	const { data: regionsData, isLoading: regionsLoading } = useSoatos();

	// Tumanlar (viloyatga qarab)
	const { data: districtsData, isLoading: districtsLoading } = useSoatos(
		regionId ? { parentId: regionId } : { parentId: -1 } // -1 bo'lsa hech narsa qaytmaydi yoki so'rov yubormaslik uchun
	);

	useEffect(() => {
		if (editingItem) {
			form.setFieldsValue({
				name: editingItem.name,
				shortName: editingItem.shortName ?? undefined,
				leaderName: editingItem.leaderName ?? undefined,
				leaderPosition: editingItem.leaderPosition ?? undefined,
				email: editingItem.email ?? undefined,
				regionId: editingItem.soato?.parentId ?? undefined,
				soatoId: editingItem.soatoId,
				typeId: editingItem.typeId,
				phoneNumber: editingItem.phoneNumber,
				stir: editingItem.stir ?? undefined,
				address: editingItem.address,
				isLock: editingItem.isLock,
			});
		} else {
			form.resetFields();
			form.setFieldsValue(createDefaults);
		}
	}, [editingItem, createDefaults, form]);

	const timeToString = (value: unknown) =>
		value && dayjs.isDayjs(value) ? (value as dayjs.Dayjs).format("HH:mm:ss") : value;

	const stringToDayjs = (value: unknown) => ({
		value: value && typeof value === "string" ? dayjs(value as string, "HH:mm:ss") : value,
	});

	return (
		<Form form={form} layout="vertical" requiredMark={false} className="mt-4">
			<Form.Item name="parentId" hidden>
				<Input />
			</Form.Item>

			<div className="grid grid-cols-2 gap-4">
				<Form.Item
					name="name"
					label="Nomi"
					rules={[
						{ required: true, message: "Nomini kiriting" },
						{ max: 255, message: "Maksimum 255 ta belgi" },
					]}
				>
					<Input placeholder="Masalan: 15-maktab" id="org-name" />
				</Form.Item>

				<Form.Item
					name="shortName"
					label="Qisqa nomi"
					rules={[{ max: 255, message: "Maksimum 255 ta belgi" }]}
					normalize={emptyToNull}
				>
					<Input placeholder="Masalan: 15-IDUM" id="org-short-name" />
				</Form.Item>

				<Form.Item
					name="typeId"
					label="Tashkilot turi"
					rules={[{ required: true, message: "Tashkilot turini tanlang" }]}
				>
					<Select
						placeholder="Tanlang"
						loading={orgTypesLoading}
						id="org-type-select"
						options={orgTypesData?.data?.items?.map((t) => ({
							value: t.id,
							label: t.name,
						}))}
						showSearch
						filterOption={(input, option) =>
							(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
						}
					/>
				</Form.Item>

				<Form.Item
					name="phoneNumber"
					label="Telefon raqam"
					rules={[
						{ required: true, message: "Telefon raqamni kiriting" },
						{
							pattern: /^\+998\d{9}$/,
							message: "To'liq 9 ta raqam kiriting",
						},
					]}
				>
					<PhoneInput id="org-phone" />
				</Form.Item>

				<Form.Item name="address" label="Manzil">
					<Input placeholder="Masalan: Toshkent sh., Chilonzor t." id="org-address" />
				</Form.Item>

				<Form.Item
					name="stir"
					label="STIR / INN"
					rules={[{ pattern: /^\d{9}$/, message: "9 ta raqam kiriting" }]}
				>
					<Input placeholder="Masalan: 123456789" id="org-stir" maxLength={9} />
				</Form.Item>

				<Form.Item
					name="leaderName"
					label="Rahbar F.I.Sh."
					rules={[{ max: 255, message: "Maksimum 255 ta belgi" }]}
					normalize={emptyToNull}
				>
					<Input placeholder="Masalan: Aliyev Vali Salimovich" id="org-leader-name" />
				</Form.Item>

				<Form.Item
					name="leaderPosition"
					label="Rahbar lavozimi"
					rules={[{ max: 255, message: "Maksimum 255 ta belgi" }]}
					normalize={emptyToNull}
				>
					<Input placeholder="Masalan: Boshqarma boshlig'i" id="org-leader-position" />
				</Form.Item>

				<Form.Item
					name="email"
					label="Email"
					rules={[{ type: "email", message: "Email formati noto'g'ri" }]}
					normalize={emptyToNull}
				>
					<Input placeholder="Masalan: info@tashkilot.uz" id="org-email" />
				</Form.Item>

				<Form.Item
					name="regionId"
					label="Viloyat / Hudud"
					rules={[{ required: true, message: "Viloyatni tanlang" }]}
				>
					<Select
						placeholder="Viloyatni tanlang"
						loading={regionsLoading}
						id="org-region-select"
						onChange={() => {
							form.setFieldValue("soatoId", undefined);
						}}
						options={regionsData?.map((s) => ({
							value: s.id,
							label: s.nameUz,
						}))}
						showSearch
						filterOption={(input, option) =>
							(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
						}
					/>
				</Form.Item>

				<Form.Item
					name="soatoId"
					label="Tuman / Shahar"
					rules={[{ required: true, message: "Tumanni tanlang" }]}
				>
					<Select
						placeholder="Tumanni tanlang"
						loading={districtsLoading}
						id="org-district-select"
						disabled={!regionId}
						options={districtsData?.map((s) => ({
							value: s.id,
							label: s.nameUz,
						}))}
						showSearch
						filterOption={(input, option) =>
							(option?.label as string)?.toLowerCase().includes(input.toLowerCase())
						}
					/>
				</Form.Item>
			</div>

			<div className="grid grid-cols-2 gap-4">
				<Form.Item
					name="defaultWorkStart"
					label="Ish boshlanish vaqti"
					normalize={timeToString}
					getValueProps={stringToDayjs}
				>
					<TimePicker format="HH:mm" className="w-full" placeholder="08:00" />
				</Form.Item>

				<Form.Item
					name="defaultWorkEnd"
					label="Ish tugash vaqti"
					normalize={timeToString}
					getValueProps={stringToDayjs}
				>
					<TimePicker format="HH:mm" className="w-full" placeholder="18:00" />
				</Form.Item>
			</div>

			<Form.Item name="defaultWorkDays" label="Ish kunlari">
				<Checkbox.Group>
					<div className="flex flex-wrap gap-2">
						{WORK_DAYS.map((day) => (
							<Checkbox key={day.value} value={day.value}>
								{day.label}
							</Checkbox>
						))}
					</div>
				</Checkbox.Group>
			</Form.Item>

			<Form.Item name="isLock" label="Bloklangan" valuePropName="checked">
				<Switch />
			</Form.Item>
		</Form>
	);
}
